import TopNav from './components/TopNav.jsx'
import Header from './components/Header.jsx'
import PillarTabs from './components/PillarTabs.jsx'
import PresenceBar from './components/PresenceBar.jsx'
import CommentPanel from './components/CommentPanel.jsx'
import MentionToast from './components/MentionToast.jsx'
// UndoToast removed 2026-04-24 — it was firing every couple seconds from
// the weekly summary autosave and felt spammy. ⌘Z still works via the
// global keyboard handler in useUndo; we just don't surface a visual
// "Undo available" toast anymore. If you want it back, re-import and
// remount below.
// import UndoToast from './components/UndoToast.jsx'
import DmPanel from './components/DmPanel.jsx'
import DmToast from './components/DmToast.jsx'
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard.jsx'
import WeeklyArchivePanel from './components/WeeklyArchivePanel.jsx'
import GuidedTour from './components/GuidedTour.jsx'
import NotificationPanel from './components/NotificationPanel.jsx'
import UpdateBanner from './components/UpdateBanner.jsx'
import ArchiveViewBanner from './components/ArchiveViewBanner.jsx'
import PillarPlaceholder from './pillars/PillarPlaceholder.jsx'
import RevenuePillar from './pillars/RevenuePillar.jsx'
import EoBuyoutsPillar from './pillars/EoBuyoutsPillar.jsx'
import GrowthPillar from './pillars/GrowthPillar.jsx'
import TeamPillar from './pillars/TeamPillar.jsx'
import AiSystemsPillar from './pillars/AiSystemsPillar.jsx'
import TeamUpdatesPillar from './pillars/TeamUpdatesPillar.jsx'
import ScorecardPillar from './pillars/ScorecardPillar.jsx'
import { useAuth } from './hooks/useAuth.js'
import { usePresenceHeartbeat } from './hooks/usePresenceHeartbeat.js'
import { useRealtimeSync } from './hooks/useRealtimeSync.js'
import { useUndo } from './hooks/useUndo.js'
// useAutoSnapshot removed 2026-04-27 — it duplicated the Wednesday 5pm PT
// worker cron and was firing on the first Monday page-load each week,
// archiving early before the team had finished writing for that week's
// leadership review. The worker cron (see `triggers.crons` in
// cloudflare-worker/wrangler.toml) is now the single source of truth.
// The hook file is kept on disk in case we ever need a manual-fallback
// path, but it is no longer mounted.
// import { useAutoSnapshot } from './hooks/useAutoSnapshot.js'
import { useDmRealtimeSync } from './queries/useDms.js'
import { useWeeklySummarySync } from './hooks/useWeeklySummarySync.js'
import { useTeamUpdateSync } from './hooks/useTeamUpdateSync.js'
import { useRevenueAutoSync } from './hooks/useRevenueAutoSync.js'
import { useEffect } from 'react'
import { useTourStore } from './store/tourStore.js'
import { useUIStore } from './store/uiStore.js'
import { PILLARS } from './data/pillars.js'

/**
 * Root app.
 *
 * The active pillar is driven by a Zustand store (useUIStore), so anything
 * in the tree — tabs, keyboard shortcuts, programmatic navigation —
 * can change which pillar is shown without prop-drilling.
 *
 * Future pillar components will be registered in a pillar router below
 * (see the switch statement). For now, every pillar renders the same
 * placeholder.
 */
export default function App() {
  const activePillarIndex = useUIStore((s) => s.activePillar)
  const activePillar = PILLARS[activePillarIndex]

  // Auth: auto-detect user from /whoami. No-op for components.
  useAuth()

  // Presence: write our heartbeat every 20s so others see we're here.
  usePresenceHeartbeat()

  // Real-time: subscribe to Ably, invalidate caches on remote changes.
  // No-ops if ABLY_API_KEY isn't set on the worker — falls back to polling.
  useRealtimeSync()

  // Undo: bind ⌘Z / Ctrl+Z keyboard shortcut app-wide
  useUndo()

  // Weekly snapshot is now driven exclusively by the Wednesday 5pm PT
  // worker cron — see the import comment for useAutoSnapshot above.

  // Real-time DMs: receive full message payloads over Ably and insert
  // directly into cache (no refetch / no propagation delay).
  useDmRealtimeSync()

  // Real-time weekly summary updates: other teammates see your field
  // changes within ~100-200ms of blur. No refetch, no delay.
  useWeeklySummarySync()

  // Same live-sync treatment for team-level updates on the Team Updates
  // pillar (GTM / Delivery / Product TLDR / Wins / Flags).
  useTeamUpdateSync()

  // HubSpot → Revenue sub-goal sync, once on mount + every 10 min.
  // Moves the sync logic that used to live in q2-goals-preview.html
  // server-side so it runs reliably from the React app. Lacy Maxwell's
  // Advisory deal and future advisory deals are rolled into Design
  // Conversions as of 2026-04-23.
  useRevenueAutoSync()

  // Auto-launch the guided tour on the user's first visit. Anyone who
  // has previously completed or skipped the tour won't see it again.
  // They can replay it any time via the 🧭 Tour button in the top nav.
  const tourCompleted = useTourStore((s) => s.completed)
  const tourOpen = useTourStore((s) => s.isOpen)
  const startTour = useTourStore((s) => s.start)
  useEffect(() => {
    if (!tourCompleted && !tourOpen) {
      // Small delay so the page has a chance to render first; the tour
      // measures DOM elements, so it needs them mounted.
      const t = setTimeout(() => startTour(), 600)
      return () => clearTimeout(t)
    }
  }, [tourCompleted, tourOpen, startTour])

  return (
    <>
      <UpdateBanner />
      <ArchiveViewBanner />
      <TopNav />
      <main className="page">
        <Header />
        <ExecutiveSummaryCard />
        <PillarTabs />
        <PresenceBar />
        <PillarContent pillarId={activePillar.id} />
      </main>
      <CommentPanel />
      <MentionToast />
      {/* UndoToast removed — see import comment above. */}
      <DmPanel />
      <DmToast />
      <WeeklyArchivePanel />
      <NotificationPanel />
      <GuidedTour />
    </>
  )
}

/**
 * PillarContent — decides which pillar component to render.
 *
 * Today every pillar returns the placeholder. As we port them in future
 * sessions, add cases here:
 *   case 'revenue':
 *     return <RevenuePillar />
 */
function PillarContent({ pillarId }) {
  switch (pillarId) {
    case 'revenue':
      return <RevenuePillar />
    case 'eo-buyouts':
      return <EoBuyoutsPillar />
    case 'growth':
      return <GrowthPillar />
    case 'team':
      return <TeamPillar />
    case 'ai':
      return <AiSystemsPillar />
    case 'team-updates':
      return <TeamUpdatesPillar />
    case 'scorecard':
      return <ScorecardPillar />
    default:
      return <PillarPlaceholder pillarId={pillarId} />
  }
}
